import { useState } from "react";
import { 
  Shield, 
  Lock, 
  FileText, 
  AlertTriangle,
  ChevronUp,
  ChevronDown,
  Scale,
  Eye,
  Database,
  UserX,
  Mail,
  Footprints,
  Sparkles
} from "lucide-react";

const Footer = () => {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const currentYear = new Date().getFullYear();

  return (
    <>
      {/* Main Footer */}
      <footer className="relative mt-auto border-t border-slate-200/50 dark:border-slate-800 bg-gradient-to-b from-white to-slate-50 dark:from-slate-900 dark:to-slate-950">
        {/* Decorative top line */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500 to-transparent"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Main content */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            
            {/* Left: Branding with Version */}
            <div className="flex items-center gap-4">
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-emerald-500 rounded-2xl blur opacity-25 group-hover:opacity-40 transition-opacity"></div>
                <div className="relative w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center shadow-lg">
                  <Footprints className="w-6 h-6 text-white" />
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-slate-900 dark:text-white text-lg tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    OrthoTrack
                  </h3>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-gradient-to-r from-blue-500 to-emerald-500 text-white">
                    PRO
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs font-mono text-slate-500 dark:text-slate-400 tracking-wider" data-testid="version-label">
                    v1.0 - 14/02/2026
                  </span>
                </div>
              </div>
            </div>
            
            {/* Center: Privacy button */}
            <button
              onClick={() => setShowPrivacy(!showPrivacy)}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100 dark:bg-slate-800/50 hover:bg-slate-200 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-700 transition-all duration-300 group hover:shadow-md"
              data-testid="privacy-toggle"
            >
              <Lock className="w-4 h-4 text-slate-500 group-hover:text-blue-500 transition-colors" />
              <span className="text-sm text-slate-600 dark:text-slate-400 group-hover:text-slate-900 dark:group-hover:text-white transition-colors">
                Privacy & Termini
              </span>
              {showPrivacy ? (
                <ChevronUp className="w-4 h-4 text-slate-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-slate-400" />
              )}
            </button>
            
            {/* Right: Copyright */}
            <div className="text-center md:text-right">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Software creato da <span className="bg-gradient-to-r from-blue-600 to-emerald-600 bg-clip-text text-transparent font-bold">Mohamed Bayoumi</span>
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                © {currentYear} Tutti i diritti riservati
              </p>
            </div>
          </div>
          
          {/* Warning banner */}
          <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border border-amber-200/50 dark:border-amber-800/30 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                  Avviso Importante
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-400/80 mt-1 leading-relaxed">
                  È severamente vietata la vendita, la distribuzione non autorizzata o la divulgazione dei dati contenuti in questo sistema. 
                  Qualsiasi violazione sarà perseguita a norma di legge.
                </p>
              </div>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Privacy Modal/Dropdown */}
      {showPrivacy && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div 
            className="w-full max-w-2xl max-h-[80vh] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom duration-300 border border-slate-200 dark:border-slate-800"
            data-testid="privacy-modal"
          >
            {/* Header */}
            <div className="sticky top-0 p-6 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 dark:from-slate-800 dark:via-slate-900 dark:to-slate-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-white/10 flex items-center justify-center">
                    <Scale className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Privacy Policy & Termini</h2>
                    <p className="text-sm text-slate-400 font-mono">v1.0 - 14/02/2026</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPrivacy(false)}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <ChevronDown className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh] space-y-6">
              
              {/* Section 1 */}
              <div className="space-y-3 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700/50">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                    <Database className="w-4 h-4 text-emerald-500" />
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-white">1. Raccolta e Utilizzo dei Dati</h3>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed pl-10">
                  Il presente software raccoglie e gestisce dati personali esclusivamente per le finalità operative 
                  del sistema gestionale. I dati trattati includono: informazioni anagrafiche, dati di contatto, 
                  codice fiscale e informazioni relative agli ordini e alle pratiche gestite.
                </p>
              </div>
              
              {/* Section 2 */}
              <div className="space-y-3 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700/50">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <Lock className="w-4 h-4 text-blue-500" />
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-white">2. Protezione dei Dati</h3>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed pl-10">
                  Tutti i dati sono protetti mediante crittografia e sistemi di sicurezza avanzati. 
                  L'accesso è limitato agli utenti autorizzati tramite autenticazione a due fattori (2FA). 
                  I dati sono conservati su server sicuri e sottoposti a backup regolari.
                </p>
              </div>
              
              {/* Section 3 */}
              <div className="space-y-3 p-4 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200/50 dark:border-red-900/50">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center">
                    <UserX className="w-4 h-4 text-red-500" />
                  </div>
                  <h3 className="font-bold text-red-700 dark:text-red-400">3. Divieto di Vendita dei Dati</h3>
                </div>
                <p className="text-sm text-red-700 dark:text-red-300 font-medium leading-relaxed pl-10">
                  È SEVERAMENTE VIETATO vendere, cedere, trasferire o divulgare a terzi qualsiasi dato 
                  contenuto in questo sistema. La violazione di questo divieto costituisce reato 
                  ai sensi del GDPR (Regolamento UE 2016/679) e del Codice Privacy italiano (D.Lgs. 196/2003) 
                  e sarà perseguita nelle sedi legali competenti.
                </p>
              </div>
              
              {/* Section 4 */}
              <div className="space-y-3 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700/50">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-violet-500/10 flex items-center justify-center">
                    <Eye className="w-4 h-4 text-violet-500" />
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-white">4. Diritti degli Interessati</h3>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed pl-10">
                  Ai sensi del GDPR, gli interessati hanno diritto di: accedere ai propri dati, 
                  richiederne la rettifica o la cancellazione, opporsi al trattamento, 
                  richiedere la portabilità dei dati. Per esercitare tali diritti, 
                  contattare l'amministratore del sistema.
                </p>
              </div>
              
              {/* Section 5 */}
              <div className="space-y-3 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700/50">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-amber-500" />
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-white">5. Proprietà Intellettuale</h3>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed pl-10">
                  Questo software è di proprietà esclusiva di Mohamed Bayoumi. 
                  È vietata la riproduzione, modifica, distribuzione o qualsiasi altro utilizzo 
                  non autorizzato del codice sorgente, dell'interfaccia grafica e di tutti i contenuti 
                  associati al software.
                </p>
              </div>
              
              {/* Section 6 */}
              <div className="space-y-3 p-4 rounded-xl bg-gradient-to-r from-blue-50 to-emerald-50 dark:from-blue-950/30 dark:to-emerald-950/30 border border-blue-200/50 dark:border-blue-900/50">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500/20 to-emerald-500/20 flex items-center justify-center">
                    <Mail className="w-4 h-4 text-blue-500" />
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-white">6. Contatti</h3>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed pl-10">
                  Per qualsiasi domanda relativa alla privacy, ai termini di servizio o per segnalazioni, 
                  è possibile contattare l'assistenza al numero <span className="font-semibold text-blue-600 dark:text-blue-400">320 706 8138</span>.
                </p>
              </div>
              
            </div>
            
            {/* Footer */}
            <div className="sticky bottom-0 p-4 bg-slate-50 dark:bg-slate-800/80 border-t border-slate-200 dark:border-slate-700 backdrop-blur-sm">
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Accettando di utilizzare questo software, l'utente accetta integralmente i presenti termini.
                </p>
                <button
                  onClick={() => setShowPrivacy(false)}
                  className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-emerald-500 hover:from-blue-600 hover:to-emerald-600 text-white text-sm font-semibold rounded-lg transition-all duration-300 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 active:scale-95"
                  data-testid="privacy-close-btn"
                >
                  Ho Capito
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Footer;
